import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface AnimalDiscoveryOverlayProps {
  visible: boolean;
  animal: {
    name: string;
    slug: string;
    fieldNote: string;
    imagePath: string;
    thumbPath?: string;
    habitat: string;
    milestone?: number;
  };
  amenityName: string;
  buildingName: string;
  onDismiss: () => void;
}

export default function AnimalDiscoveryOverlay({
  visible,
  animal,
  amenityName,
  buildingName,
  onDismiss,
}: AnimalDiscoveryOverlayProps) {
  const { t } = useTranslation('globe');
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    if (visible) {
      navigator.vibrate?.(100);
      window.gtag?.('event', 'globe_discovery', {
        animal_slug: animal.slug,
        animal_name: animal.name,
        ...(animal.milestone ? { milestone: animal.milestone } : {}),
      });
      if (animal.milestone) {
        window.gtag?.('event', 'globe_milestone', { milestone_count: animal.milestone });
      }
      document.body.style.overflow = 'hidden';
      const timer = setTimeout(onDismiss, 5000);
      const handleKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onDismiss();
      };
      document.addEventListener('keydown', handleKey);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('keydown', handleKey);
        document.body.style.overflow = '';
      };
    }
  }, [visible, onDismiss]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label={t('discovery.title', { defaultValue: 'New species discovered!' })}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={onDismiss}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Content */}
          <motion.div
            className="relative flex flex-col items-center z-10 px-6 py-8 mx-4 rounded-3xl max-w-sm w-full"
            style={{ backgroundColor: '#FAFAF8' }}
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ type: 'spring', damping: 14, stiffness: 180, delay: 0.1 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Animal image */}
            <motion.div
              className="w-full flex justify-center mb-4"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}
            >
              {!imgError ? (
                <img
                  src={animal.thumbPath || animal.imagePath}
                  alt={animal.name}
                  className="max-h-[40vh] w-auto object-contain rounded-2xl"
                  style={{ filter: 'saturate(1.1)' }}
                  onError={() => setImgError(true)}
                />
              ) : (
                <div
                  className="w-32 h-32 rounded-2xl flex items-center justify-center text-5xl"
                  style={{ backgroundColor: '#EDE0C8' }}
                >
                  🦎
                </div>
              )}
            </motion.div>

            {/* Animal name */}
            <motion.p
              className="text-xl font-bold text-center"
              style={{ color: '#3D2B1F' }}
              initial={{ y: 12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {animal.name}
            </motion.p>

            {/* Amenity info */}
            <motion.p
              className="text-xs text-center mt-1"
              style={{ color: '#8B7355' }}
              initial={{ y: 12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.55 }}
            >
              {amenityName}
              {buildingName ? `, ${buildingName}` : ''}
            </motion.p>

            {/* Field note */}
            <motion.p
              className="text-sm italic text-center mt-1 px-4"
              style={{ color: '#6B5744' }}
              initial={{ y: 12, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              {animal.fieldNote}
            </motion.p>

            {/* Footer text */}
            <motion.p
              className="text-xs text-center mt-4"
              style={{ color: '#8B7355' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              {t('discovery.footer', {
                defaultValue: 'This species is now in your Reserve.',
              })}
            </motion.p>

            {/* Confetti for milestones 25+ */}
            {animal.milestone && animal.milestone >= 25 && (
              <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
                {Array.from({ length: 20 }, (_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-2 h-2 rounded-full"
                    style={{
                      backgroundColor: ['#D4AF37', '#5B7C5A', '#8B7355', '#C19A6B', '#2D6A4F'][i % 5],
                      left: `${10 + Math.random() * 80}%`,
                      top: '-10%',
                    }}
                    animate={{ y: '120vh', rotate: 360 * (Math.random() > 0.5 ? 1 : -1), opacity: [1, 1, 0] }}
                    transition={{ duration: 2 + Math.random() * 2, delay: Math.random() * 0.5, ease: 'easeIn' }}
                  />
                ))}
              </div>
            )}

            {/* Milestone */}
            {animal.milestone && (
              <motion.p
                className="text-xs font-medium text-center mt-2 px-3 py-1 rounded-full"
                style={{ color: '#5B4A2E', backgroundColor: '#EDE0C8' }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                {t(`milestone.${animal.milestone}`, {
                  defaultValue: '{{count}} species discovered.',
                  count: animal.milestone,
                })}
              </motion.p>
            )}

            {/* Dismiss button */}
            <motion.button
              className="mt-5 px-6 py-2 rounded-full text-sm font-semibold"
              style={{ backgroundColor: '#2D6A4F', color: '#FAFAF8' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.0 }}
              onClick={onDismiss}
              aria-label={t('discovery.dismiss', { defaultValue: 'Got it' })}
            >
              {t('discovery.dismiss', { defaultValue: 'Got it' })}
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
